import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AchievementShowcase = ({ achievements }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Achievements', icon: 'Award' },
    { id: 'completion', label: 'Completion', icon: 'CheckCircle' },
    { id: 'performance', label: 'Performance', icon: 'Zap' },
    { id: 'streak', label: 'Streak', icon: 'Flame' },
    { id: 'special', label: 'Special', icon: 'Star' }
  ];

  const filteredAchievements = achievements?.filter(achievement => 
    selectedCategory === 'all' || achievement?.category === selectedCategory
  );

  const getAchievementIcon = (type) => {
    const iconMap = {
      'first-mission': 'Target',
      'speed-demon': 'Zap',
      'perfectionist': 'Star',
      'streak-master': 'Flame',
      'code-warrior': 'Sword',
      'bug-hunter': 'Bug',
      'automation-expert': 'Cog',
      'milestone': 'Trophy'
    };
    return iconMap?.[type] || 'Award';
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'from-slate-400 to-slate-600';
      case 'rare':
        return 'from-blue-400 to-blue-600';
      case 'epic':
        return 'from-purple-400 to-purple-600';
      case 'legendary':
        return 'from-yellow-400 to-yellow-600';
      default:
        return 'from-gray-400 to-gray-600';
    }
  };

  const shareAchievement = (achievement) => {
    const text = `I just earned the "${achievement?.title}" achievement on Selenium Sandbox! 🎉`;
    const url = window.location?.origin;
    
    if (navigator.share) {
      navigator.share({
        title: 'Achievement Unlocked!',
        text: text,
        url: url
      });
    } else {
      navigator.clipboard?.writeText(`${text} ${url}`);
      // You could show a toast notification here
    }
  };

  return (
    <div className="bg-card rounded-lg border border-subtle p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Achievements</h2>
        <div className="text-sm text-muted-foreground">
          {achievements?.filter(a => a?.earned)?.length} / {achievements?.length} earned
        </div>
      </div>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <Button
            key={category?.id}
            variant={selectedCategory === category?.id ? "default" : "outline"}
            size="sm"
            iconName={category?.icon}
            iconPosition="left"
            onClick={() => setSelectedCategory(category?.id)}
          >
            {category?.label}
          </Button>
        ))}
      </div>
      {/* Achievement Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAchievements?.map((achievement) => (
          <div
            key={achievement?.id}
            className={`relative p-4 rounded-lg border transition-all duration-300 ${
              achievement?.earned
                ? 'bg-gradient-to-br from-muted/50 to-muted/30 border-primary/50 hover:border-primary' :'bg-muted/20 border-subtle opacity-60'
            }`}
          >
            {/* Achievement Badge */}
            <div className="flex items-start gap-3 mb-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  achievement?.earned
                    ? `bg-gradient-to-br ${getRarityColor(achievement?.rarity)}`
                    : 'bg-muted'
                }`}
              >
                <Icon
                  name={getAchievementIcon(achievement?.type)}
                  size={24}
                  color={achievement?.earned ? "white" : "var(--color-muted-foreground)"}
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold mb-1 ${
                  achievement?.earned ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {achievement?.title}
                </h3>
                <p className={`text-sm ${
                  achievement?.earned ? 'text-muted-foreground' : 'text-muted-foreground/60'
                }`}>
                  {achievement?.description}
                </p>
              </div>
            </div>

            {/* Achievement Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className={`px-2 py-1 rounded-full ${
                  achievement?.earned ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {achievement?.rarity}
                </span>
                {achievement?.earned && (
                  <span className="text-muted-foreground">
                    {new Date(achievement.earnedAt)?.toLocaleDateString()}
                  </span>
                )}
              </div>

              {achievement?.progress && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Progress</span>
                    <span>{achievement?.progress?.current} / {achievement?.progress?.target}</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{
                        width: `${Math.min((achievement?.progress?.current / achievement?.progress?.target) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}

              {achievement?.earned && (
                <div className="flex items-center gap-2 pt-2">
                  <div className="flex items-center gap-1 text-xs text-success">
                    <Icon name="Plus" size={12} />
                    <span>+{achievement?.xpReward} XP</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Share"
                    onClick={() => shareAchievement(achievement)}
                    className="ml-auto"
                  >
                    Share
                  </Button>
                </div>
              )}
            </div>

            {/* Earned Badge */}
            {achievement?.earned && (
              <div className="absolute top-2 right-2">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center">
                  <Icon name="Check" size={14} color="white" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {filteredAchievements?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No achievements found in this category.</p>
        </div>
      )}
    </div>
  );
};

export default AchievementShowcase;